import remarkGfm from "remark-gfm"
import ReactMarkdown from "react-markdown"
import { createContext, useContext, useState } from "react"

import { Dialog, DialogContent } from "@/components/ui/dialog"

interface InfoCardData {
  name: string
  guidance: string
}

interface InfoCardContextType {
  isOpen: boolean
  infoCardData: InfoCardData
  openInfoCard: (data: InfoCardData) => void
  closeInfoCard: () => void
  toggleInfoCard: () => void
}

const InfoCardContext = createContext<InfoCardContextType | undefined>(undefined)

export function InfoCardProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [infoCardData, setInfoCardData] = useState<InfoCardData>({ name: "", guidance: "" })

  const openInfoCard = (data: InfoCardData) => {
    setInfoCardData(data)
    setIsOpen(true)
  }

  const closeInfoCard = () => {
    setIsOpen(false)
  }

  const toggleInfoCard = () => {
    setIsOpen(!isOpen)
  }

  return (
    <InfoCardContext.Provider value={{ isOpen, infoCardData, openInfoCard, closeInfoCard, toggleInfoCard }}>
      {children}
    </InfoCardContext.Provider>
  )
}

export function useInfoCard() {
  const context = useContext(InfoCardContext)
  if (context == null) throw new Error("useInfoCard must be used within InfoCardProvider")

  return context
}

export function InfoCard() {
  const { isOpen, infoCardData, closeInfoCard } = useInfoCard()

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeInfoCard()}>
      <DialogContent
        className="fixed top-0 right-0 m-0 h-full w-[30rem] max-w-none translate-x-0 translate-y-0 gap-0 rounded-none border-l p-0 shadow-lg"
        style={{
          transform: "none",
          animationDuration: "300ms",
          left: "auto",
        }}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="mt-6 flex-1 space-y-4 overflow-y-auto p-4">
          <div>
            <div className="overflow-y-auto p-3">
              {infoCardData.guidance != null && infoCardData.guidance !== "" ? (
                <div className="prose prose-sm max-w-none text-sm leading-relaxed break-words prose-headings:text-gray-800 prose-p:text-gray-600 prose-strong:text-gray-800 prose-li:text-gray-600">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{infoCardData.guidance}</ReactMarkdown>
                </div>
              ) : (
                <div className="text-sm text-gray-500 italic">No guidance provided</div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
